/**
 * `/admin/accounts` — Comptes : utilisateurs, organisations, abonnements.
 *
 * Onglet unique regroupant :
 *  - Liste utilisateurs (recherche par email/nom)
 *  - Liste organisations (recherche par nom/slug/owner) avec consultation
 *    supervisée journalisée
 */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { AdminPageHeader, AdminCard } from "./ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader,
  DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import StatusBadge from "@/components/common/StatusBadge";
import SmartLoader from "@/components/common/SmartLoader";
import {
  Users, Building2, Search, Eye, RefreshCw, AlertTriangle, CheckCircle2, XCircle,
  Pencil, KeyRound, Mail,
} from "lucide-react";
import { toast } from "sonner";

const fmt = (v) => (v == null ? "—" : String(v));

export default function AdminAccountsPage() {
  return (
    <div className="animate-fade-up">
      <AdminPageHeader
        overline="Comptes"
        title="Utilisateurs, organisations & abonnements"
        description="Recherchez tout compte de la plateforme. La consultation d'une organisation cliente est journalisée avec motif obligatoire."
        testId="admin-accounts-header"
      />
      <Tabs defaultValue="orgs" className="text-white">
        <TabsList className="mb-6 bg-white/5 border border-white/10">
          <TabsTrigger value="orgs" data-testid="admin-accounts-tab-orgs">
            <Building2 className="h-3.5 w-3.5 mr-1.5" /> Organisations
          </TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-accounts-tab-users">
            <Users className="h-3.5 w-3.5 mr-1.5" /> Utilisateurs
          </TabsTrigger>
        </TabsList>
        <TabsContent value="orgs"><OrganizationsPanel /></TabsContent>
        <TabsContent value="users"><UsersPanel /></TabsContent>
      </Tabs>
    </div>
  );
}

function OrganizationsPanel() {
  const [q, setQ] = useState("");
  const [includeDemo, setIncludeDemo] = useState(false);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [selected, setSelected] = useState(null);
  const [reason, setReason] = useState("");
  const [reasonOpen, setReasonOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await api.get("/platform-admin/organizations", {
        params: { q: q || undefined, include_demo: includeDemo, limit: 100 },
      });
      setOrgs(data.items);
    } catch (e) {
      setErr(e.response?.data?.detail || "Impossible de charger les organisations");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [includeDemo]);

  const askReason = (org) => {
    setSelected(org); setReason(""); setDetail(null); setReasonOpen(true);
  };

  const consult = async () => {
    if (reason.trim().length < 8) {
      toast.error("Le motif doit contenir au moins 8 caractères");
      return;
    }
    try {
      const { data } = await api.post(
        `/platform-admin/organizations/${selected.id}/consult`,
        { reason: reason.trim() },
      );
      setDetail(data);
      toast.success("Accès journalisé — consultation en lecture seule");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Consultation refusée");
    }
  };

  return (
    <div className="space-y-4">
      <AdminCard className="p-4">
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[240px]">
            <Label className="text-xs text-white/60">Recherche</Label>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/40" />
              <Input value={q} onChange={(e) => setQ(e.target.value)}
                     placeholder="Nom, slug, e-mail propriétaire…"
                     className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-white/30"
                     data-testid="admin-orgs-search" />
            </div>
          </div>
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer text-white/70">
            <input type="checkbox" checked={includeDemo}
                   onChange={(e) => setIncludeDemo(e.target.checked)}
                   data-testid="admin-orgs-include-demo" />
            Inclure les comptes démo
          </label>
          <Button type="submit" disabled={loading}
                  className="bg-accent text-accent-foreground hover:bg-accent/90"
                  data-testid="admin-orgs-search-btn">
            <RefreshCw className={`h-3 w-3 mr-1 ${loading ? "animate-spin" : ""}`} />
            Rechercher
          </Button>
        </form>
      </AdminCard>

      {loading || err ? (
        <SmartLoader label="Chargement des organisations…" onRetry={load} error={err} />
      ) : (
        <AdminCard className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-xs uppercase text-white/50">
              <tr className="text-left">
                <th className="px-3 py-3">Organisation</th>
                <th className="px-3 py-3">Propriétaire</th>
                <th className="px-3 py-3">Formule</th>
                <th className="px-3 py-3 text-right">Immeubles</th>
                <th className="px-3 py-3 text-right">Locataires</th>
                <th className="px-3 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {orgs.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-10 text-center text-white/50">
                  Aucune organisation correspondante.
                </td></tr>
              )}
              {orgs.map((o) => (
                <tr key={o.id} data-testid={`admin-org-row-${o.id}`} className="hover:bg-white/5">
                  <td className="px-3 py-3">
                    <div className="font-medium text-white flex items-center gap-2">
                      {o.name}
                      {o.is_demo && <StatusBadge tone="warning">Démo</StatusBadge>}
                    </div>
                    <div className="text-xs text-white/50">{o.slug}</div>
                  </td>
                  <td className="px-3 py-3">
                    <div className="text-white/90">{o.owner?.email || <span className="text-white/40">—</span>}</div>
                    <div className="text-xs text-white/50">
                      {o.owner ? `${o.owner.first_name || ""} ${o.owner.last_name || ""}` : ""}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-xs">
                    <StatusBadge tone={o.subscription_status === "trial" ? "info" : "success"}>
                      {o.subscription_plan || "—"}
                    </StatusBadge>
                  </td>
                  <td className="px-3 py-3 text-right font-mono text-white/90">{o.counts.buildings}</td>
                  <td className="px-3 py-3 text-right font-mono text-white/90">{o.counts.tenants}</td>
                  <td className="px-3 py-3">
                    <Button size="sm" variant="outline"
                            className="border-white/20 text-white hover:bg-white/10"
                            onClick={() => askReason(o)}
                            data-testid={`admin-org-consult-${o.id}`}>
                      <Eye className="h-3 w-3 mr-1" />Consulter
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </AdminCard>
      )}

      <Dialog open={reasonOpen} onOpenChange={(o) => { setReasonOpen(o); if (!o) setDetail(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              Consultation supervisée — {selected?.name}
            </DialogTitle>
            <DialogDescription>
              Cette action sera journalisée avec votre e-mail, votre IP, la date, l&apos;organisation ciblée
              et le motif ci-dessous. Aucune modification n&apos;est possible.
            </DialogDescription>
          </DialogHeader>
          {!detail ? (
            <div className="space-y-3">
              <Label htmlFor="admin-consult-reason" className="text-xs">
                Motif (min. 8 caractères)
              </Label>
              <Textarea id="admin-consult-reason" value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Ex : Réponse ticket #4321 — vérification bail signé"
                        data-testid="admin-consult-reason" />
              <DialogFooter>
                <Button variant="outline" onClick={() => setReasonOpen(false)}>Annuler</Button>
                <Button onClick={consult} disabled={reason.trim().length < 8}
                        data-testid="admin-consult-submit">
                  Consulter en lecture seule
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <ConsultDetail detail={detail} onClose={() => setReasonOpen(false)} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ConsultDetail({ detail, onClose }) {
  const org = detail.organization;
  const c = detail.counts || {};
  const owner = detail.owner || {};
  return (
    <div className="space-y-4 text-sm">
      <div>
        <div className="text-xs text-muted-foreground">Organisation</div>
        <div className="font-semibold">{org.name} <span className="text-xs text-muted-foreground">({org.slug})</span></div>
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div><span className="text-muted-foreground">Formule</span><br /><StatusBadge tone="success">{org.subscription_plan || "—"}</StatusBadge></div>
        <div><span className="text-muted-foreground">Statut</span><br /><StatusBadge>{org.subscription_status || "—"}</StatusBadge></div>
        <div><span className="text-muted-foreground">Propriétaire</span><br />{owner.email || "—"}</div>
        <div><span className="text-muted-foreground">Créée le</span><br />{fmt(org.created_at)?.slice(0, 10)}</div>
      </div>
      <div className="grid grid-cols-4 gap-2 text-center">
        {[
          ["Immeubles", c.buildings], ["Lots", c.lots],
          ["Locataires", c.tenants], ["Baux", c.leases],
        ].map(([label, val]) => (
          <div key={label} className="rounded-xl bg-secondary/40 p-2">
            <div className="text-[10px] text-muted-foreground uppercase">{label}</div>
            <div className="font-mono text-lg">{val ?? 0}</div>
          </div>
        ))}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Fermer</Button>
      </DialogFooter>
    </div>
  );
}

function UsersPanel() {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const { data } = await api.get("/platform-admin/users", {
        params: { q: q || undefined, limit: 100 },
      });
      setItems(data.items || []);
    } catch (e) {
      setErr(e.response?.data?.detail || "Impossible de charger les utilisateurs");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="space-y-4">
      <AdminCard className="p-4">
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[240px]">
            <Label className="text-xs text-white/60">Recherche</Label>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/40" />
              <Input value={q} onChange={(e) => setQ(e.target.value)}
                     placeholder="E-mail, nom, prénom…"
                     className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-white/30"
                     data-testid="admin-users-search" />
            </div>
          </div>
          <Button type="submit" disabled={loading}
                  className="bg-accent text-accent-foreground hover:bg-accent/90"
                  data-testid="admin-users-search-btn">
            <RefreshCw className={`h-3 w-3 mr-1 ${loading ? "animate-spin" : ""}`} />
            Rechercher
          </Button>
        </form>
      </AdminCard>

      {loading || err ? (
        <SmartLoader label="Chargement des utilisateurs…" onRetry={load} error={err} />
      ) : (
        <AdminCard className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-xs uppercase text-white/50">
              <tr className="text-left">
                <th className="px-3 py-3">Nom</th>
                <th className="px-3 py-3">E-mail</th>
                <th className="px-3 py-3">Rôle plateforme</th>
                <th className="px-3 py-3">E-mail vérifié</th>
                <th className="px-3 py-3">Inscrit le</th>
                <th className="px-3 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {items.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-10 text-center text-white/50">
                  Aucun utilisateur correspondant.
                </td></tr>
              )}
              {items.map((u) => (
                <tr key={u.id} className="hover:bg-white/5" data-testid={`admin-user-row-${u.id}`}>
                  <td className="px-3 py-3 text-white">{u.first_name} {u.last_name}</td>
                  <td className="px-3 py-3 text-white/80 font-mono text-xs">{u.email}</td>
                  <td className="px-3 py-3">
                    <StatusBadge tone={
                      u.platform_role === "platform_admin" ? "accent"
                      : u.platform_role === "founder" ? "info"
                      : "neutral"
                    }>
                      {u.platform_role || "user"}
                    </StatusBadge>
                  </td>
                  <td className="px-3 py-3">
                    {u.email_verified
                      ? <CheckCircle2 className="h-4 w-4 text-emerald-400 inline" />
                      : <XCircle className="h-4 w-4 text-rose-400 inline" />}
                  </td>
                  <td className="px-3 py-3 text-xs text-white/60">{fmt(u.created_at)?.slice(0, 10)}</td>
                  <td className="px-3 py-3 text-right">
                    <Button size="sm" variant="outline"
                            className="border-white/20 text-white hover:bg-white/10"
                            onClick={() => setEditing(u)}
                            data-testid={`admin-user-edit-${u.id}`}>
                      <Pencil className="h-3 w-3 mr-1" />Modifier
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </AdminCard>
      )}

      <UserEditDialog
        user={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
    </div>
  );
}

function UserEditDialog({ user, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [showPasswordInput, setShowPasswordInput] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        email: user.email || "",
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        platform_role: user.platform_role || "user",
      });
      setNewPassword("");
      setShowPasswordInput(false);
    }
  }, [user]);

  if (!user || !form) return null;

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/platform-admin/users/${user.id}`, form);
      toast.success("Utilisateur mis à jour");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible d'enregistrer");
    } finally { setSaving(false); }
  };

  const sendReset = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/platform-admin/users/${user.id}/send-reset`);
      toast.success(`Lien envoyé à ${data.email_sent_to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Envoi impossible");
    } finally { setSaving(false); }
  };

  const setPassword = async () => {
    if (newPassword.length < 10) {
      toast.error("Le mot de passe doit contenir au moins 10 caractères");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/platform-admin/users/${user.id}/set-password`, { new_password: newPassword });
      toast.success("Mot de passe défini");
      setNewPassword(""); setShowPasswordInput(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de définir le mot de passe");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="admin-user-edit-dialog">
        <DialogHeader>
          <DialogTitle>Modifier {user.email}</DialogTitle>
          <DialogDescription>
            Modifications journalisées automatiquement. Un changement de rôle
            ou d&apos;e-mail révoque toutes les sessions actives de l&apos;utilisateur.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Prénom</Label>
              <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                     data-testid="admin-user-edit-firstname" />
            </div>
            <div>
              <Label className="text-xs">Nom</Label>
              <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                     data-testid="admin-user-edit-lastname" />
            </div>
          </div>
          <div>
            <Label className="text-xs">E-mail</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                   data-testid="admin-user-edit-email" />
          </div>
          <div>
            <Label className="text-xs">Rôle plateforme</Label>
            <Select value={form.platform_role} onValueChange={(v) => setForm({ ...form, platform_role: v })}>
              <SelectTrigger data-testid="admin-user-edit-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="user">Utilisateur client</SelectItem>
                <SelectItem value="founder">Fondateur (staff + client)</SelectItem>
                <SelectItem value="platform_admin">Super Administrateur</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground mt-1">
              <strong>Super Admin</strong> = plateforme uniquement. <strong>Fondateur</strong>
              {" "}= plateforme + client. <strong>Utilisateur</strong> = client uniquement.
            </p>
          </div>

          <div className="border-t pt-3 space-y-2">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Sécurité</div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={sendReset} disabled={saving}
                      data-testid="admin-user-send-reset">
                <Mail className="h-3 w-3 mr-1" />Envoyer un lien de réinitialisation
              </Button>
              <Button variant="outline" onClick={() => setShowPasswordInput((v) => !v)}
                      data-testid="admin-user-toggle-set-password">
                <KeyRound className="h-3 w-3 mr-1" />Définir un mot de passe
              </Button>
            </div>
            {showPasswordInput && (
              <div className="pt-2">
                <Label className="text-xs">Nouveau mot de passe (min. 10 caractères)</Label>
                <div className="flex gap-2 mt-1">
                  <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                         placeholder="••••••••••••"
                         data-testid="admin-user-new-password" />
                  <Button onClick={setPassword} disabled={saving}
                          data-testid="admin-user-set-password">
                    Enregistrer
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={save} disabled={saving} data-testid="admin-user-save">
            {saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
